import { config } from 'js-xdr';

var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _bundleSettings, _items, _bundleData, _formatBundleSettings, formatBundleSettings_fn, _formatBundleData, formatBundleData_fn, _isValueInRange, isValueInRange_fn, _unique, unique_fn;
function toLineItemProperty(bundle) {
  const opts = {
    variantId: xdr.Uint64.fromString(bundle.variantId.toString()),
    version: bundle.version || Math.floor(Date.now() / 1e3),
    items: bundle.items.map((item) => {
      return new xdr.BundleItem({
        collectionId: xdr.Uint64.fromString(item.collectionId.toString()),
        productId: xdr.Uint64.fromString(item.productId.toString()),
        variantId: xdr.Uint64.fromString(item.variantId.toString()),
        sku: item.sku || "",
        quantity: item.quantity,
        ext: new xdr.BundleItemExt(0)
      });
    }),
    ext: new xdr.BundleExt(0)
  };
  const xdrBundle = new xdr.Bundle(opts);
  return xdr.BundleEnvelope.envelopeTypeBundle(xdrBundle).toXDR("base64");
}
const xdr = config(
  (xdr2) => {
    xdr2.enum("EnvelopeType", {
      envelopeTypeBundle: 0
    });
    xdr2.typedef("Uint32", xdr2.uint());
    xdr2.typedef("Uint64", xdr2.uhyper());
    xdr2.union("BundleItemExt", {
      switchOn: xdr2.int(),
      switchName: "v",
      switches: [[0, xdr2.void()]],
      arms: {}
    });
    xdr2.struct("BundleItem", [
      ["collectionId", xdr2.lookup("Uint64")],
      ["productId", xdr2.lookup("Uint64")],
      ["variantId", xdr2.lookup("Uint64")],
      ["sku", xdr2.string()],
      ["quantity", xdr2.lookup("Uint32")],
      ["ext", xdr2.lookup("BundleItemExt")]
    ]);
    xdr2.union("BundleExt", {
      switchOn: xdr2.int(),
      switchName: "v",
      switches: [[0, xdr2.void()]],
      arms: {}
    });
    xdr2.struct("Bundle", [
      ["variantId", xdr2.lookup("Uint64")],
      ["items", xdr2.varArray(xdr2.lookup("BundleItem"), 500)],
      ["version", xdr2.lookup("Uint32")],
      ["ext", xdr2.lookup("BundleExt")]
    ]);
    xdr2.union("BundleEnvelope", {
      switchOn: xdr2.lookup("EnvelopeType"),
      switchName: "type",
      switches: [["envelopeTypeBundle", "v1"]],
      arms: {
        v1: xdr2.lookup("Bundle")
      }
    });
  }
);
class BundleSelectionValidator {
  constructor(selectionItems, bundleData) {
    __privateAdd(this, _formatBundleSettings);
    __privateAdd(this, _formatBundleData);
    __privateAdd(this, _isValueInRange);
    __privateAdd(this, _unique);
    __privateAdd(this, _bundleSettings, void 0);
    __privateAdd(this, _items, void 0);
    __privateAdd(this, _bundleData, void 0);
    __privateSet(this, _bundleSettings, __privateMethod(this, _formatBundleSettings, formatBundleSettings_fn).call(this, bundleData));
    __privateSet(this, _bundleData, __privateMethod(this, _formatBundleData, formatBundleData_fn).call(this, bundleData));
    __privateSet(this, _items, selectionItems);
  }
  isInitialDataValid() {
    if (!__privateGet(this, _bundleData)) {
      throw "Bundle data does not exist for the given product.";
    }
    if (!__privateGet(this, _bundleSettings)) {
      throw "Bundle settings do not exist for the given product.";
    }
    if (!Array.isArray(__privateGet(this, _items)) || !__privateGet(this, _items).length) {
      throw "No bundle selection items provided.";
    }
    return true;
  }
  isValidVariant(externalVariantId) {
    if (!__privateGet(this, _bundleSettings)?.variantsSettings[externalVariantId]) {
      throw `The ${externalVariantId} bundle variant is invalid.`;
    }
    return true;
  }
  isSelectionInVariantRange(externalVariantId) {
    const total = __privateGet(this, _items).reduce((acc, current) => acc + current.quantity, 0);
    const ranges = __privateGet(this, _bundleSettings)?.variantsSettings[externalVariantId].ranges || [];
    if (!__privateMethod(this, _isValueInRange, isValueInRange_fn).call(this, ranges, total)) {
      throw "The total number of products does not fall within the required variant range.";
    }
    return true;
  }
  areItemsInMaxPerItemBound() {
    const maxPerItem = __privateGet(this, _bundleSettings)?.bundleSettings.maxPerItem || 0;
    const invalidProducts = __privateGet(this, _items).filter(({ quantity }) => quantity > maxPerItem);
    if (maxPerItem && invalidProducts.length) {
      const invalidIds = __privateMethod(this, _unique, unique_fn).call(this, invalidProducts.map(({ externalProductId }) => externalProductId)).join(", ");
      throw `The selected products (${invalidIds}) exceed the maximum limit of ${maxPerItem} items per product.`;
    }
    return true;
  }
  areSelectedCollectionsValid(externalVariantId) {
    const variantSettings = __privateGet(this, _bundleSettings)?.variantsSettings[externalVariantId];
    const invalidCollections = __privateGet(this, _items).filter(({ collectionId }) => !variantSettings?.optionSources[collectionId]);
    if (invalidCollections.length) {
      const invalidIds = __privateMethod(this, _unique, unique_fn).call(this, invalidCollections.map(({ collectionId }) => collectionId)).join(", ");
      throw `The collections (${invalidIds}) are no longer valid for the bundle variant (${externalVariantId}).`;
    }
    return true;
  }
  areItemsQuantitiesValidForTheCollection(externalVariantId) {
    const totalSelectedByCollection = __privateGet(this, _items).reduce((acc, current) => {
      acc[current.collectionId] = current.quantity + (acc[current.collectionId] || 0);
      return acc;
    }, {});
    const variantSettings = __privateGet(this, _bundleSettings)?.variantsSettings[externalVariantId];
    const invalidItems = Object.values(variantSettings?.optionSources || {}).filter((collection) => {
      const quantity = totalSelectedByCollection[collection.collectionId] || 0;
      return !__privateMethod(this, _isValueInRange, isValueInRange_fn).call(this, [collection], quantity);
    });
    if (!variantSettings || invalidItems.length) {
      const invalidIds = __privateMethod(this, _unique, unique_fn).call(this, invalidItems.map(({ collectionId }) => collectionId)).join(", ");
      throw `The selection exceeds the limits for the collections (${invalidIds}).`;
    }
    return true;
  }
  areSelectedProductsValid(externalVariantId) {
    const bundleVariantCollections = __privateGet(this, _bundleSettings)?.variantsSettings[externalVariantId]?.optionSources || {};
    const variantCollections = Object.values(bundleVariantCollections).reduce(
      (acc, optionSource) => {
        const collection = __privateGet(this, _bundleData)?.collections[optionSource.collectionId];
        if (collection) {
          acc[optionSource.collectionId] = collection;
        }
        return acc;
      },
      {}
    );
    if (!Object.values(variantCollections).length) {
      throw `No collections found for the bundle variant (${externalVariantId}).`;
    }
    const missingProducts = __privateGet(this, _items).filter(
      ({ externalProductId, collectionId }) => !variantCollections[collectionId]?.products[externalProductId]
    );
    if (missingProducts.length) {
      const invalidIds = __privateMethod(this, _unique, unique_fn).call(this, missingProducts.map(({ externalProductId }) => externalProductId)).join(", ");
      throw `The products (${invalidIds}) are no longer valid for the bundle product.`;
    }
    return true;
  }
  isBundleSelectionValid(externalVariantId) {
    this.isInitialDataValid();
    this.isValidVariant(externalVariantId);
    this.isSelectionInVariantRange(externalVariantId);
    this.areItemsInMaxPerItemBound();
    this.areSelectedCollectionsValid(externalVariantId);
    this.areItemsQuantitiesValidForTheCollection(externalVariantId);
    this.areSelectedProductsValid(externalVariantId);
    return true;
  }
}
_bundleSettings = new WeakMap();
_items = new WeakMap();
_bundleData = new WeakMap();
_formatBundleSettings = new WeakSet();
formatBundleSettings_fn = function(bundleData) {
  try {
    const bundleSettings = bundleData.bundle_settings;
    return {
      bundleSettings: {
        maxPerItem: bundleSettings.max_quantity_per_variant,
        isCustomizable: bundleSettings.is_customizable
      },
      variantsSettings: bundleSettings.variants.reduce((acc, variant) => {
        if (!variant.enabled)
          return acc;
        let ranges = [{ min: variant.items_count, max: variant.items_count }];
        if (!variant.items_count) {
          ranges = variant.ranges.map(({ quantity_max, quantity_min }) => ({
            min: quantity_min,
            max: quantity_max
          }));
        }
        acc[variant.external_variant_id] = {
          externalVariantId: variant.external_variant_id,
          optionSources: variant.option_sources.reduce((acc2, optionSource) => {
            acc2[optionSource.option_source_id] = {
              min: optionSource.quantity_min,
              max: optionSource.quantity_max,
              collectionId: optionSource.option_source_id
            };
            return acc2;
          }, {}),
          ranges
        };
        return acc;
      }, {})
    };
  } catch (error) {
    return null;
  }
};
_formatBundleData = new WeakSet();
formatBundleData_fn = function(bundleData) {
  try {
    const collections = Object.values(bundleData.collections).reduce((acc, current) => {
      acc[current.id] = {
        ...current,
        products: current.products.reduce((productAcc, product) => {
          productAcc[product.id] = product;
          return productAcc;
        }, {})
      };
      return acc;
    }, {});
    return { ...bundleData, collections };
  } catch (error) {
    return null;
  }
};
_isValueInRange = new WeakSet();
isValueInRange_fn = function(ranges, value) {
  const matchedRanges = ranges.filter(({ min, max }) => {
    const minValue = min ?? value;
    const maxValue = max ?? value;
    return minValue <= value && value <= maxValue;
  });
  return !!matchedRanges.length;
};
_unique = new WeakSet();
unique_fn = function(ids) {
  return Array.from(new Set(ids));
};

export { BundleSelectionValidator, toLineItemProperty };
//# sourceMappingURL=bundle.js.map
